import { Fragment, useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { confirmer } from '../components/Confirm';
import {
  api,
  type PendingQuote, type QuoteDetail, type QuoteAddress,
  type PendingOrder, type OrderDetail,
  type PendingInvoice, type InvoiceDetail,
  type DealRow, type DealDetail, type DealCounts, type DealFilter,
  type SaleSource, type OrigineFilter, type SentEmail,
  type OrderDeliveryFilter, type OrderInvoiceFilter, type OrderGroupFilter, type PendingOrders,
  type InvoiceStateFilter, type InvoicePayFilter, downloadPdf } from '../api';
import { useChat } from '../ChatEngine';
import Pager from '../components/Pager';
import { useSort, SortTh, SearchInput, FilterSelect } from '../components/ListSort';
import { ProductLink, stripCode } from '../components/Links';
import { useIsMobile, useScrollToDetail } from '../useIsMobile';
import Stepper from '../components/Stepper';
import { InvoiceStatusBadge, DeliveryStatusBadge, InvoiceBadge } from '../components/StatusBadge';
import { BusinessDate } from '../components/BusinessDate';

// ---------- helpers partagés ----------
function fmtMoney(n: number, currency: string | null): string {
  return `${n.toLocaleString('fr-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${currency ? ' ' + currency : ''}`;
}
function fmtDate(d: string | false): string {
  if (!d) return '—';
  const day = String(d).slice(0, 10);
  const parts = day.split('-');
  return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : day;
}

/** Odoo rend ses dates en UTC, sans fuseau : la marquer avant de l'afficher. */
function fmtDateTime(d: string): string {
  const iso = String(d).replace(' ', 'T') + 'Z';
  const t = new Date(iso);
  if (Number.isNaN(t.getTime())) return fmtDate(d);
  return t.toLocaleString('fr-CH', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

type Toast = { kind: 'ok' | 'err'; text: string } | null;

const PAGE_SIZE = 25;

/**
 * Liste paginée d'un onglet. Le serveur ne renvoie qu'une tranche et le total du
 * jeu filtré ; `load` doit être stable (référence de `api.*` ou `useCallback`),
 * sinon le rechargement bouclerait.
 */
/**
 * Origine d'une vente. Odoo produit le même document dans les deux cas (le brouillon
 * d'un sale.order EST le devis) : seul le tag « Agent IA » posé par le pipeline les
 * distingue. Le badge n'apparaît donc que pour l'agent — pas de badge = saisie humaine.
 */
function SourceBadge({ source }: { source: SaleSource }) {
  if (source !== 'agent') return null;
  return (
    <span className="src-badge" title="Créé automatiquement depuis un email client">🤖 Agent</span>
  );
}

/** Ligne « Origine » des panneaux de détail, explicite dans les deux sens. */
function SourceRow({ source }: { source: SaleSource }) {
  return (
    <div><span>Origine</span><strong>
      {source === 'agent' ? '🤖 Agent — email client' : '✍️ Saisie manuelle'}
    </strong></div>
  );
}

function usePagedList<T, C = undefined>(
  load: (opts: { limit: number; offset: number }) => Promise<{ items: T[]; total: number; counts?: C }>,
) {
  const [items, setItems] = useState<T[]>([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState<C | undefined>(undefined);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await load({ limit: PAGE_SIZE, offset });
      setItems(r.items);
      setTotal(r.total);
      setCounts(r.counts);
      // Dernière ligne d'une page traitée (devis validé, facture encaissée…) :
      // sans ce repli, l'écran resterait sur une page devenue vide.
      if (r.items.length === 0 && offset > 0) setOffset(Math.max(0, offset - PAGE_SIZE));
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [load, offset]);
  useEffect(() => { reload(); }, [reload]);

  return { items, total, counts, offset, setOffset, loading, error, reload };
}

function AddressLines({ addr }: { addr: QuoteAddress | null }) {
  if (!addr || addr.lines.length === 0) return <span className="devis-addr-empty">Non renseignée</span>;
  return (
    <div className="devis-addr-body">
      <strong>{addr.name}</strong>
      {addr.lines.map((l, i) => <span key={i}>{l}</span>)}
    </div>
  );
}

function AddressBlock({ detail }: { detail: QuoteDetail | OrderDetail }) {
  if (!detail.invoice_address && !detail.shipping_address) return null;
  return (
    <div className="devis-addresses">
      {detail.same_address ? (
        <div className="devis-addr">
          <span className="devis-addr-title">Facturation &amp; livraison</span>
          <AddressLines addr={detail.invoice_address ?? detail.shipping_address} />
        </div>
      ) : (
        <>
          <div className="devis-addr">
            <span className="devis-addr-title">Facturation</span>
            <AddressLines addr={detail.invoice_address} />
          </div>
          <div className="devis-addr devis-addr-ship">
            <span className="devis-addr-title">Livraison</span>
            <AddressLines addr={detail.shipping_address} />
          </div>
        </>
      )}
    </div>
  );
}

function LinesTable({ lines }: { lines: QuoteDetail['lines'] }) {
  return (
    <table className="devis-lines">
      <thead>
        <tr>
          <th>Article</th>
          <th className="num">Qté</th>
          <th className="num">P.U.</th>
          <th className="num">Sous-total</th>
        </tr>
      </thead>
      <tbody>
        {lines.map((l) => (
          <tr key={l.id}>
            <td><ProductLink id={l.product_id}>{l.name}</ProductLink></td>
            <td className="num">{l.qty}</td>
            <td className="num">{fmtMoney(l.price_unit, null)}</td>
            <td className="num">{fmtMoney(l.subtotal, null)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * Les emails déjà partis pour ce document (ticket #39).
 *
 * L'envoi laisse désormais une trace dans Odoo ; la montrer ICI est ce qui
 * permet de répondre à « est-ce que la confirmation est partie ? » sans ouvrir
 * Odoo. Le bloc ne s'affiche pas quand rien n'est encore parti : une fiche
 * muette dit déjà « aucun envoi », une ligne « 0 email » n'ajouterait rien.
 */
function SentEmails({ emails }: { emails: SentEmail[] | undefined }) {
  if (!emails || emails.length === 0) return null;
  return (
    <div className="devis-emails">
      <span className="devis-addr-title">Emails envoyés</span>
      {emails.map((e) => (
        <div key={e.messageId} className={`devis-email ${e.status}`}>
          <span className="devis-email-mark">
            {e.status === 'sent' ? '✓' : e.status === 'failed' ? '⚠️' : '⏳'}
          </span>
          <span className="devis-email-body">
            <strong>{e.subject ?? 'Email'}</strong>
            <span>
              {fmtDateTime(e.date)} — {e.to.join(', ') || 'destinataire inconnu'}
              {e.attachments > 0 && ` · ${e.attachments} pièce${e.attachments > 1 ? 's' : ''} jointe${e.attachments > 1 ? 's' : ''}`}
            </span>
            {e.status === 'failed' && (
              <span className="devis-email-fail">Non remis{e.failure ? ` : ${e.failure}` : ''}</span>
            )}
          </span>
        </div>
      ))}
    </div>
  );
}

function Totals({ ut, tax, total, currency }: { ut: number; tax: number; total: number; currency: string | null }) {
  return (
    <div className="devis-totals">
      <div><span>Hors taxe</span><strong>{fmtMoney(ut, currency)}</strong></div>
      <div><span>Taxes</span><strong>{fmtMoney(tax, currency)}</strong></div>
      <div className="total"><span>Total</span><strong>{fmtMoney(total, currency)}</strong></div>
    </div>
  );
}

/**
 * Élément ouvert = paramètre d'URL. C'est ce qui rend une vente, un devis ou une
 * facture atteignables depuis un autre écran (`/ventes?vente=42`) et ce qui garde
 * le bouton « précédent » utile. Chaque onglet a sa clé.
 */
function useSelectedParam(key: 'devis' | 'commande' | 'facture' | 'vente') {
  const [params, setParams] = useSearchParams();
  const selectedId = Number(params.get(key)) || null;
  const setSelectedId = (id: number | null) => {
    const p = new URLSearchParams(params);
    if (id) p.set(key, String(id)); else p.delete(key);
    setParams(p, { replace: true });
  };
  return [selectedId, setSelectedId] as const;
}

// Ouvre (ou reprend) la conversation liée à un document Odoo, dans la popin :
// on reste sur la liste, la fiche du document est toujours sous les yeux.
function useAskAssistant(setToast: (t: Toast) => void) {
  const chat = useChat();
  return async (docKey: string, title: string, prefix: string) => {
    try {
      // Le contexte du document est TOUJOURS pré-rempli, même si la conversation
      // existe déjà : sans lui, « Demander une modification » ouvrait un champ vide
      // dès le deuxième passage sur la même fiche. Les chiffres ont pu changer entre
      // les deux échanges, les redonner vaut mieux que de compter sur l'historique.
      const { conversation } = await api.openDocConversation(docKey, title);
      chat.openDock({ conv: conversation.id, draft: prefix });
    } catch (e: any) {
      setToast({ kind: 'err', text: e.message });
    }
  };
}

// ---------- Onglet Devis ----------
function DevisTab() {
  const { sort, dir, toggle } = useSort();
  const [q, setQ] = useState('');
  const { items, total, offset, setOffset, loading, error, reload } =
    usePagedList<PendingQuote>(
      useCallback(
        (o: { limit: number; offset: number }) =>
          api.listPendingQuotes({ ...o, sort, dir, q }),
        [sort, dir, q],
      ),
    );
  const onSort = (c: string) => { toggle(c); setOffset(0); };
  const [selectedId, setSelectedId] = useSelectedParam('devis');
  useScrollToDetail(selectedId);
  const isMobile = useIsMobile();
  const [detail, setDetail] = useState<QuoteDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [toast, setToast] = useState<Toast>(null);
  const ask = useAskAssistant(setToast);

  useEffect(() => {
    if (selectedId == null) { setDetail(null); return; }
    setDetailLoading(true);
    setDetail(null);
    api.getQuote(selectedId).then(setDetail).catch((e) => setToast({ kind: 'err', text: e.message })).finally(() => setDetailLoading(false));
  }, [selectedId]);

  function askForQuote() {
    if (!detail) return;
    const lines = detail.lines.map((l) => `  - ${l.qty}× ${l.name} — P.U. ${fmtMoney(l.price_unit, null)} — sous-total ${fmtMoney(l.subtotal, detail.currency)}`).join('\n');
    const prefix =
      `Je travaille sur le devis Odoo **${detail.name}** (sale.order id ${detail.id}), en attente de validation.\n` +
      `- Client : ${detail.partner ?? '—'}${detail.client_order_ref ? ` (réf. client ${detail.client_order_ref})` : ''}\n` +
      `- Total actuel : ${fmtMoney(detail.amount_total, detail.currency)} (HT ${fmtMoney(detail.amount_untaxed, detail.currency)}, taxes ${fmtMoney(detail.amount_tax, detail.currency)})\n` +
      `- Lignes :\n${lines}\n\nMa demande : `;
    ask(`sale.order:${detail.id}`, `Devis ${detail.name}`, prefix);
  }

  /**
   * Confirmer le devis, en prévenant le client ou non (ticket #40).
   *
   * Les deux gestes sont séparés comme sur l'onglet Suivi : la commande peut
   * avoir été convenue par téléphone, ou l'adresse rester à vérifier. Ici ils
   * restent deux BOUTONS et non deux étapes, parce qu'une fois confirmé le devis
   * quitte cet onglet — n'offrir que « Valider » obligerait à passer par Suivi
   * pour l'envoi le plus courant.
   */
  async function validate(send: boolean) {
    if (selectedId == null || validating) return;
    const msg = send
      ? 'Confirmer ce devis en bon de commande et envoyer la confirmation au client ?'
      : 'Confirmer ce devis en bon de commande ? Le client ne sera PAS prévenu — l’envoi reste possible depuis l’onglet Suivi.';
    if (!await confirmer(msg)) return;
    setValidating(true);
    setToast(null);
    try {
      const res = await api.validateQuote(selectedId, send);
      setSelectedId(null);
      await reload();
      const ok = send
        ? `${res.name} confirmé et envoyé au client. ✅ Retrouvez-le dans l'onglet Commandes.`
        : `${res.name} confirmé, sans email. ✅ Retrouvez-le dans l'onglet Commandes.`;
      setToast({ kind: 'ok', text: res.warning ? `${res.name} confirmé. ⚠️ ${res.warning}` : ok });
    } catch (e: any) {
      setToast({ kind: 'err', text: e.message });
    } finally {
      setValidating(false);
    }
  }

  async function annuler() {
    if (selectedId == null || cancelling || validating || !detail) return;
    if (!await confirmer(`Annuler le devis ${detail.name} ?\n\nIl passera en « annulé » dans Odoo — il n'est pas supprimé — et quittera la file des devis à valider.`)) return;
    setCancelling(true);
    setToast(null);
    try {
      const res = await api.cancelQuote(selectedId);
      setSelectedId(null);
      await reload();
      setToast({ kind: 'ok', text: `${res.name} annulé.` });
    } catch (e: any) {
      setToast({ kind: 'err', text: e.message });
    } finally {
      setCancelling(false);
    }
  }

  const detailPanel = selectedId == null ? null : (
    <aside className="devis-detail">
      <button className="devis-detail-close" onClick={() => setSelectedId(null)} title="Fermer">×</button>
      {detailLoading && <div className="devis-empty">Chargement du devis…</div>}
      {detail && (
        <>
          <h2>{detail.name}</h2>
          <Stepper stage={0} />
          <div className="devis-meta">
            <div><span>Client</span><strong>{detail.partner ?? '—'}</strong></div>
            <div><span>Email</span>{detail.partner_email ? <strong>{detail.partner_email}</strong> : <strong className="warn">aucun email</strong>}</div>
            <SourceRow source={detail.source} />
            <div><span>Date</span><strong><BusinessDate value={detail.date_order} createdAt={detail.created_at} /></strong></div>
            {detail.client_order_ref && <div><span>Réf. client</span><strong>{detail.client_order_ref}</strong></div>}
          </div>
          <AddressBlock detail={detail} />
          <LinesTable lines={detail.lines} />
          <Totals ut={detail.amount_untaxed} tax={detail.amount_tax} total={detail.amount_total} currency={detail.currency} />
          {!detail.partner_email && (
            <div className="devis-warn">
              ⚠️ Ce client n'a pas d'adresse email : rien ne peut lui être envoyé. Renseignez-la sur sa
              fiche depuis l'écran <Link className="link-btn" to="/clients">Clients</Link>, ou confirmez
              la commande sans le prévenir.
            </div>
          )}
          <div className="devis-actions">
            <a className="devis-odoo" href={`/api/orders/${detail.id}/pdf`}>⬇ Télécharger le PDF</a>
            <a className="devis-odoo" href={detail.web_url} target="_blank" rel="noopener noreferrer">↗ Ouvrir dans Odoo</a>
            <button className="devis-ask" onClick={askForQuote}>💬 Demander une modification</button>
            <button className="devis-cancel" onClick={annuler} disabled={validating || cancelling}>
              {cancelling ? 'Annulation…' : '✕ Annuler le devis'}
            </button>
            <button className="devis-ask" onClick={() => validate(false)} disabled={validating || cancelling}>
              {validating ? 'Validation…' : '▶ Valider sans prévenir le client'}
            </button>
            <button className="devis-validate" onClick={() => validate(true)}
              disabled={validating || cancelling || !detail.partner_email}
              title={detail.partner_email ? undefined : 'Ce client n’a pas d’adresse email'}>
              {validating ? 'Validation…' : 'Valider & envoyer au client'}
            </button>
          </div>
        </>
      )}
    </aside>
  );

  return (
    <>
      {toast && <div className={`devis-toast ${toast.kind}`} onClick={() => setToast(null)}>{toast.text}</div>}
      <div className="ventes-toolbar">
        {/* Aucun filtre ici : l'onglet ne montre QUE les propales saisies à la
            main. Les commandes transcrites d'un email par l'agent sont dans
            l'onglet Commandes, sous « À valider » (2026-10-28) — d'où le retrait
            du filtre d'origine, puis de « Tous / À valider » (2026-11-04), qui
            reposait sur l'activité du pipeline et ne pouvait donc plus rien
            afficher sur un onglet réservé au manuel. */}
        <SearchInput value={q} onChange={(v) => { setQ(v); setOffset(0); }}
          placeholder="N° de devis, réf. ou client…" />
        <button className="devis-refresh" onClick={reload} disabled={loading}>↻ Rafraîchir</button>
      </div>
      <div className="devis-body">
        <div className="devis-list">
          {/* Élément ouvert par lien direct : il n'est pas forcément dans la page
              listée — sur mobile, où le détail se déplie sous sa ligne, il faut
              alors l'afficher au-dessus de la liste. */}
          {isMobile && selectedId != null && !items.some((x: { id: number }) => x.id === selectedId) && detailPanel}
          {loading && <div className="devis-empty">Chargement…</div>}
          {error && !loading && <div className="devis-empty err">Erreur : {error}</div>}
          {!loading && !error && items.length === 0 && <div className="devis-empty">Aucun devis à afficher.</div>}
          {!loading && !error && items.length > 0 && (
            <table>
              <thead><tr>
                <SortTh col="numero" label="N°" sort={sort} dir={dir} onSort={onSort} />
                <SortTh col="client" label="Client" sort={sort} dir={dir} onSort={onSort} />
                <SortTh col="montant" label="Montant" className="num" sort={sort} dir={dir} onSort={onSort} />
                <SortTh col="date" label="Date" sort={sort} dir={dir} onSort={onSort} />
              </tr></thead>
              <tbody>
                {items.map((q) => (
                  <Fragment key={q.id}>
                  <tr className={q.id === selectedId ? 'selected' : ''} onClick={() => setSelectedId(q.id)}>
                    <td className="mono cell-title">{q.name} <SourceBadge source={q.source} /></td>
                    <td data-label="Client">{q.partner ?? '—'}</td>
                    <td className="num" data-label="Montant">{fmtMoney(q.amount_total, q.currency)}</td>
                    <td data-label="Date"><BusinessDate value={q.date_order} createdAt={q.created_at} /></td>
                  </tr>
                  {/* Mobile : pas de colonne de droite, le détail s'ouvre sous la carte touchée. */}
                  {isMobile && q.id === selectedId && (
                    <tr className="detail-row detail-row-panel">
                      <td colSpan={4}>{detailPanel}</td>
                    </tr>
                  )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          )}
          {!loading && !error && (
            <Pager
              total={total} limit={PAGE_SIZE} offset={offset}
              onOffset={(o) => { setOffset(o); setSelectedId(null); }}
              unit={['devis', 'devis']}
            />
          )}
        </div>

        {!isMobile && detailPanel}
      </div>
    </>
  );
}

// ---------- Onglet Commandes ----------
function CommandesTab() {
  const { sort, dir, toggle } = useSort();
  const [q, setQ] = useState('');
  const [livraison, setLivraison] = useState<OrderDeliveryFilter>('tout');
  const [facturation, setFacturation] = useState<OrderInvoiceFilter>('tout');
  // Groupe affiché : les commandes reçues par email attendent une validation,
  // les confirmées attendent la livraison — deux files de travail distinctes.
  const [groupe, setGroupe] = useState<OrderGroupFilter>('tout');
  const [groupes, setGroupes] = useState<PendingOrders['groupes'] | null>(null);
  const { items, total, offset, setOffset, loading, error, reload } = usePagedList<PendingOrder>(
    useCallback(
      (o: { limit: number; offset: number }) =>
        api.listPendingOrders({ ...o, sort, dir, q, livraison, facturation, groupe })
          .then((r) => { setGroupes(r.groupes); return r; }),
      [sort, dir, q, livraison, facturation, groupe],
    ),
  );
  // Changer de tri renvoie en page 1 : garder l'offset afficherait la page 3
  // d'un classement qui n'existe plus.
  const onSort = (c: string) => { toggle(c); setOffset(0); };
  const [selectedId, setSelectedId] = useSelectedParam('commande');
  useScrollToDetail(selectedId);
  const isMobile = useIsMobile();
  const [detail, setDetail] = useState<OrderDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [deliverQty, setDeliverQty] = useState<Record<number, number>>({});
  const [toast, setToast] = useState<Toast>(null);
  const ask = useAskAssistant(setToast);

  useEffect(() => {
    if (selectedId == null) { setDetail(null); return; }
    setDetailLoading(true);
    setDetail(null);
    api.getOrder(selectedId).then(setDetail).catch((e) => setToast({ kind: 'err', text: e.message })).finally(() => setDetailLoading(false));
  }, [selectedId]);
  // Quantités à livrer par défaut = stock disponible.
  useEffect(() => {
    if (!detail) { setDeliverQty({}); return; }
    const init: Record<number, number> = {};
    for (const dl of detail.deliver_lines) init[dl.move_id] = dl.available;
    setDeliverQty(init);
  }, [detail]);

  function askForOrder() {
    if (!detail) return;
    const lines = detail.lines.map((l) => `  - ${l.qty}× ${l.name} — P.U. ${fmtMoney(l.price_unit, null)} — sous-total ${fmtMoney(l.subtotal, detail.currency)}`).join('\n');
    const prefix =
      `Je travaille sur la commande Odoo **${detail.name}** (sale.order id ${detail.id}), confirmée, en attente de livraison/facturation.\n` +
      `- Client : ${detail.partner ?? '—'}\n` +
      `- Total : ${fmtMoney(detail.amount_total, detail.currency)}\n` +
      `- Lignes :\n${lines}\n\nMa demande : `;
    ask(`sale.order:${detail.id}`, `Commande ${detail.name}`, prefix);
  }

  async function validate() {
    if (selectedId == null || validating || !detail) return;
    const deliveries = detail.deliver_lines.length
      ? detail.deliver_lines.map((dl) => ({ move_id: dl.move_id, qty: deliverQty[dl.move_id] ?? dl.available }))
      : undefined;
    const total = deliveries ? deliveries.reduce((s, d) => s + (d.qty || 0), 0) : 1;
    if (total <= 0) { setToast({ kind: 'err', text: 'Aucune quantité à livrer.' }); return; }
    if (!await confirmer('Valider la commande ? Les quantités saisies seront livrées (sortie de stock), le reste passera en reliquat (backorder), puis la facture du livré sera créée.')) return;
    setValidating(true);
    setToast(null);
    try {
      const res = await api.validateOrder(selectedId, deliveries);
      await reload();
      setSelectedId(null);
      const bo = res.backorder ? ' (reliquat créé pour le reste)' : '';
      // Commande déjà facturée : la livraison seule a été enregistrée. Annoncer
      // « facture créée » serait faux, et c'est exactement le cas où l'écran
      // produisait un avoir avant le correctif du 2026-10-26.
      setToast(res.already_invoiced
        ? { kind: 'ok', text: `${res.name} livrée${bo}. Facture ${res.invoice_name ?? ''} déjà émise — aucune nouvelle facture. ✅` }
        : { kind: 'ok', text: `${res.name} livrée${bo}. ${res.invoice_name ? `Facture ${res.invoice_name} créée` : 'Facture créée'} — à valider dans l'onglet Factures. ✅` });
    } catch (e: any) {
      setToast({ kind: 'err', text: e.message });
    } finally {
      setValidating(false);
    }
  }

  const detailPanel = selectedId == null ? null : (
    <aside className="devis-detail">
      <button className="devis-detail-close" onClick={() => setSelectedId(null)} title="Fermer">×</button>
      {detailLoading && <div className="devis-empty">Chargement de la commande…</div>}
      {detail && (
        <>
          <h2>{detail.name}</h2>
          <Stepper stage={detail.stage} source={detail.source} />
          <div className="devis-meta">
            <div><span>Client</span><strong>{detail.partner ?? '—'}</strong></div>
            <div><span>Livraison</span><strong><DeliveryStatusBadge status={detail.delivery_status} /></strong></div>
            <div><span>Facturation</span><strong><InvoiceStatusBadge status={detail.invoice_status} /></strong></div>
            <div><span>Date</span><strong><BusinessDate value={detail.date_order} createdAt={detail.created_at} /></strong></div>
            {detail.client_order_ref && <div><span>Réf. client</span><strong>{detail.client_order_ref}</strong></div>}
          </div>
          <AddressBlock detail={detail} />
          {detail.deliver_lines.length > 0 ? (
            <>
              <p className="deliver-hint">Choisissez les quantités à livrer (le reste part en reliquat) :</p>
              <table className="devis-lines">
                <thead><tr><th>Article</th><th className="num">Cmd</th><th className="num">Dispo</th><th className="num">À livrer</th></tr></thead>
                <tbody>
                  {detail.deliver_lines.map((dl) => (
                    <tr key={dl.move_id}>
                      <td><ProductLink id={dl.product_id}>{stripCode(dl.product) || '—'}</ProductLink></td>
                      <td className="num">{dl.ordered}</td>
                      <td className={`num ${dl.available < dl.ordered ? 'stock-short' : ''}`}>{dl.available}</td>
                      <td className="num">
                        <input className="deliver-input" type="number" min={0} max={dl.ordered}
                          value={deliverQty[dl.move_id] ?? dl.available}
                          onChange={(e) => setDeliverQty((q) => ({ ...q, [dl.move_id]: Math.max(0, Math.min(Number(e.target.value) || 0, dl.ordered)) }))} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          ) : (
            <LinesTable lines={detail.lines} />
          )}
          <Totals ut={detail.amount_untaxed} tax={detail.amount_tax} total={detail.amount_total} currency={detail.currency} />
          <SentEmails emails={detail.emails} />
          <div className="devis-actions">
            <a className="devis-odoo" href={`/api/orders/${detail.id}/pdf`}>⬇ Télécharger le PDF</a>
            {(detail.delivery_status === 'full' || detail.delivery_status === 'partial') && (
              <a className="devis-odoo" href={`/api/orders/${detail.id}/delivery-pdf`}>⬇ Bon de livraison (PDF)</a>
            )}
            <a className="devis-odoo" href={detail.web_url} target="_blank" rel="noopener noreferrer">↗ Ouvrir dans Odoo</a>
            <button className="devis-ask" onClick={askForOrder}>💬 Demander une modification</button>
            <button className="devis-validate" onClick={validate} disabled={validating}>
              {validating ? 'Traitement…' : 'Valider la commande (livrer + facturer)'}
            </button>
          </div>
        </>
      )}
    </aside>
  );

  return (
    <>
      {toast && <div className={`devis-toast ${toast.kind}`} onClick={() => setToast(null)}>{toast.text}</div>}
      <div className="ventes-toolbar">
        {/* Deux files de travail distinctes : valider ce qui est arrivé par
            email, puis livrer et facturer ce qui est confirmé. Les compteurs
            portent sur la recherche et les filtres en cours. */}
        <div className="suivi-filters">
          {([['tout', 'Toutes', 'tout'], ['a_valider', 'À valider', 'a_valider'], ['confirmee', 'Confirmées', 'confirmees']] as [OrderGroupFilter, string, keyof NonNullable<typeof groupes>][]).map(([k, lbl, ck]) => (
            <button key={k} className={`suivi-filter ${groupe === k ? 'active' : ''}`}
              onClick={() => { setGroupe(k); setOffset(0); setSelectedId(null); }}>
              {lbl}{groupes ? ` (${groupes[ck]})` : ''}
            </button>
          ))}
        </div>
        <SearchInput value={q} onChange={(v) => { setQ(v); setOffset(0); }}
          placeholder="N° de commande, réf. ou client…" />
        <FilterSelect label="Livraison" value={livraison}
          onChange={(v) => { setLivraison(v); setOffset(0); }}
          options={[['tout', 'Toutes'], ['pending', 'Non livrée'], ['partial', 'Partielle'], ['full', 'Livrée']]} />
        <FilterSelect label="Facturation" value={facturation}
          onChange={(v) => { setFacturation(v); setOffset(0); }}
          options={[['tout', 'Toutes'], ['to invoice', 'À facturer'], ['no', 'Rien à facturer']]} />
        <button className="devis-refresh" onClick={reload} disabled={loading}>↻ Rafraîchir</button>
      </div>
      <div className="devis-body">
        <div className="devis-list">
          {/* Élément ouvert par lien direct : il n'est pas forcément dans la page
              listée — sur mobile, où le détail se déplie sous sa ligne, il faut
              alors l'afficher au-dessus de la liste. */}
          {isMobile && selectedId != null && !items.some((x: { id: number }) => x.id === selectedId) && detailPanel}
          {loading && <div className="devis-empty">Chargement…</div>}
          {error && !loading && <div className="devis-empty err">Erreur : {error}</div>}
          {!loading && !error && items.length === 0 && <div className="devis-empty">🎉 Aucune commande en attente.</div>}
          {!loading && !error && items.length > 0 && (
            <table>
              <thead><tr>
                <SortTh col="numero" label="N°" sort={sort} dir={dir} onSort={onSort} />
                <SortTh col="client" label="Client" sort={sort} dir={dir} onSort={onSort} />
                <SortTh col="montant" label="Montant" className="num" sort={sort} dir={dir} onSort={onSort} />
                <SortTh col="date" label="Date" sort={sort} dir={dir} onSort={onSort} />
                <SortTh col="livraison" label="Livraison" sort={sort} dir={dir} onSort={onSort} />
                <SortTh col="facturation" label="Facturation" sort={sort} dir={dir} onSort={onSort} />
              </tr></thead>
              <tbody>
                {items.map((o, i) => (
                  <Fragment key={o.id}>
                  {/* Sur « Toutes », un intertitre marque le passage d'un groupe
                      à l'autre. La page étant à cheval sur les deux, il peut n'y
                      en avoir aucun, un seul ou les deux — d'où la comparaison
                      avec la ligne précédente plutôt qu'un rendu en deux blocs. */}
                  {groupe === 'tout' && (i === 0 || items[i - 1]!.groupe !== o.groupe) && (
                    <tr className="group-row">
                      <td colSpan={6}>
                        {o.groupe === 'a_valider' ? 'À valider' : 'Confirmées'}
                      </td>
                    </tr>
                  )}
                  <tr className={o.id === selectedId ? 'selected' : ''} onClick={() => setSelectedId(o.id)}>
                    <td className="mono cell-title">{o.name} <SourceBadge source={o.source} /></td>
                    <td data-label="Client">{o.partner ?? '—'}</td>
                    <td className="num" data-label="Montant">{fmtMoney(o.amount_total, o.currency)}</td>
                    <td data-label="Date"><BusinessDate value={o.date_order} createdAt={o.created_at} /></td>
                    <td data-label="Livraison"><DeliveryStatusBadge status={o.delivery_status} /></td>
                    <td data-label="Facturation"><InvoiceStatusBadge status={o.invoice_status} /></td>
                  </tr>
                  {/* Mobile : pas de colonne de droite, le détail s'ouvre sous la carte touchée. */}
                  {isMobile && o.id === selectedId && (
                    <tr className="detail-row detail-row-panel">
                      <td colSpan={6}>{detailPanel}</td>
                    </tr>
                  )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          )}
          {!loading && !error && (
            <Pager
              total={total} limit={PAGE_SIZE} offset={offset}
              onOffset={(o) => { setOffset(o); setSelectedId(null); }}
              unit={['commande', 'commandes']}
            />
          )}
        </div>

        {!isMobile && detailPanel}
      </div>
    </>
  );
}

// ---------- Onglet Factures ----------
function FacturesTab() {
  const { sort, dir, toggle } = useSort();
  const [q, setQ] = useState('');
  const [etat, setEtat] = useState<InvoiceStateFilter>('tout');
  const [paiement, setPaiement] = useState<InvoicePayFilter>('tout');
  const { items, total, offset, setOffset, loading, error, reload } = usePagedList<PendingInvoice>(
    useCallback(
      (o: { limit: number; offset: number }) => api.listInvoices({ ...o, sort, dir, q, etat, paiement }),
      [sort, dir, q, etat, paiement],
    ),
  );
  const onSort = (c: string) => { toggle(c); setOffset(0); };
  const [selectedId, setSelectedId] = useSelectedParam('facture');
  useScrollToDetail(selectedId);
  const isMobile = useIsMobile();
  const [detail, setDetail] = useState<InvoiceDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [busy, setBusy] = useState<null | 'post' | 'send' | 'pay' | 'unpay'>(null);
  const [toast, setToast] = useState<Toast>(null);
  const ask = useAskAssistant(setToast);


  function loadDetail(id: number) {
    setDetailLoading(true);
    api.getInvoice(id).then(setDetail).catch((e) => setToast({ kind: 'err', text: e.message })).finally(() => setDetailLoading(false));
  }
  useEffect(() => {
    if (selectedId == null) { setDetail(null); return; }
    setDetail(null);
    loadDetail(selectedId);
  }, [selectedId]);

  function askForInvoice() {
    if (!detail) return;
    const lines = detail.lines.map((l) => `  - ${l.qty}× ${l.name} — P.U. ${fmtMoney(l.price_unit, null)} — sous-total ${fmtMoney(l.subtotal, detail.currency)}`).join('\n');
    const prefix =
      `Je travaille sur la facture Odoo **${detail.name}** (account.move id ${detail.id}).\n` +
      `- Client : ${detail.partner ?? '—'}\n` +
      `- Total : ${fmtMoney(detail.amount_total, detail.currency)} (restant dû ${fmtMoney(detail.amount_residual, detail.currency)})\n` +
      `- Lignes :\n${lines}\n\nMa demande : `;
    ask(`account.move:${detail.id}`, `Facture ${detail.name}`, prefix);
  }

  async function run(action: 'post' | 'send' | 'pay' | 'unpay', confirmMsg: string, okMsg: (r: any) => string) {
    if (selectedId == null || busy) return;
    if (!await confirmer(confirmMsg)) return;
    setBusy(action);
    setToast(null);
    try {
      const fn = action === 'post' ? api.postInvoice
        : action === 'send' ? api.sendInvoice
          : action === 'unpay' ? api.unpayInvoice
            : api.payInvoice;
      const res = await fn(selectedId);
      setToast({ kind: 'ok', text: okMsg(res) });
      loadDetail(selectedId);   // rafraîchir l'état affiché
      reload();                 // et la liste (badges)
    } catch (e: any) {
      setToast({ kind: 'err', text: e.message });
    } finally {
      setBusy(null);
    }
  }

  const detailPanel = selectedId == null ? null : (
    <aside className="devis-detail">
      <button className="devis-detail-close" onClick={() => setSelectedId(null)} title="Fermer">×</button>
      {detailLoading && <div className="devis-empty">Chargement de la facture…</div>}
      {detail && (
        <>
          <h2>{detail.name}</h2>
          <Stepper stage={detail.stage} />
          <div className="devis-meta">
            <div><span>Client</span><strong>{detail.partner ?? '—'}</strong></div>
            <div><span>État</span><strong><InvoiceBadge state={detail.state} payment={detail.payment_state} overdue={detail.overdue} /></strong></div>
            {detail.invoice_origin && <div><span>Origine</span><strong>{detail.invoice_origin}</strong></div>}
            {detail.client_ref && <div><span>Réf. client</span><strong>{detail.client_ref}</strong></div>}
            <div><span>Date</span><strong><BusinessDate value={detail.invoice_date} createdAt={detail.created_at} /></strong></div>
            <div><span>Échéance</span><strong>{fmtDate(detail.invoice_date_due)}</strong></div>
            {detail.state === 'posted' && detail.payment_state !== 'paid' && (
              <div><span>Restant dû</span><strong>{fmtMoney(detail.amount_residual, detail.currency)}</strong></div>
            )}
          </div>
          <LinesTable lines={detail.lines} />
          <Totals ut={detail.amount_untaxed} tax={detail.amount_tax} total={detail.amount_total} currency={detail.currency} />
          {!detail.partner_email && detail.state === 'posted' && (
            <div className="devis-warn">⚠️ Ce client n'a pas d'adresse email : l'envoi de la facture ne sera pas possible.</div>
          )}
          <SentEmails emails={detail.emails} />
          <div className="devis-actions">
            <a className="devis-odoo" href={`/api/invoices/${detail.id}/pdf`}>⬇ Télécharger le PDF</a>
            {/* Le bon de livraison n'est relié à la facture que par la commande :
                une facture peut en avoir plusieurs (livraison partielle) ou aucun
                (service, facture saisie à la main) — d'où un lien par bon. */}
            {detail.pickings.map((p) => (
              <button key={p.id} className="devis-odoo" onClick={() => downloadPdf(`/api/stock/pickings/${p.id}/pdf`)}>
                ⬇ Bon de livraison {p.name}
              </button>
            ))}
            <a className="devis-odoo" href={detail.web_url} target="_blank" rel="noopener noreferrer">↗ Ouvrir dans Odoo</a>
            <button className="devis-ask" onClick={askForInvoice}>💬 Demander une modification</button>
            {detail.state === 'draft' && (
              <button className="devis-validate" disabled={busy != null}
                onClick={() => run('post', 'Valider (comptabiliser) cette facture ?', (r) => `Facture ${r.name ?? ''} validée. ✅`)}>
                {busy === 'post' ? 'Validation…' : 'Valider la facture'}
              </button>
            )}
            {/* Un encaissement saisi par erreur se défait ici : sans ce bouton il
                fallait ouvrir la comptabilité Odoo, ce que la règle produit exclut. */}
            {detail.state === 'posted' && (detail.payment_state === 'paid' || detail.payment_state === 'in_payment') && (
              <button className="devis-ask" disabled={busy != null}
                onClick={() => run('unpay',
                  'Annuler l\u2019encaissement de cette facture ? Elle redeviendra « non payée ».',
                  (r) => `Encaissement annulé — ${fmtMoney(r.amount_residual, detail.currency)} à nouveau dû. ✅`)}>
                {busy === 'unpay' ? 'Annulation…' : '↩ Annuler le paiement'}
              </button>
            )}
            {detail.state === 'posted' && detail.payment_state !== 'paid' && detail.payment_state !== 'in_payment' && (
              <>
                <button className="devis-ask" disabled={busy != null || !detail.partner_email}
                  onClick={() => run('send', `Envoyer la facture PDF à ${detail.partner_email} ?`, () => 'Facture envoyée au client. ✅')}>
                  {busy === 'send' ? 'Envoi…' : '✉️ Envoyer au client'}
                </button>
                <button className="devis-validate" disabled={busy != null}
                  onClick={() => run('pay', 'Enregistrer le paiement de cette facture (marquer comme payée) ?', () => 'Paiement enregistré. ✅')}>
                  {busy === 'pay' ? 'Enregistrement…' : 'Marquer comme payée'}
                </button>
              </>
            )}
          </div>
        </>
      )}
    </aside>
  );

  return (
    <>
      {toast && <div className={`devis-toast ${toast.kind}`} onClick={() => setToast(null)}>{toast.text}</div>}
      <div className="ventes-toolbar">
        <SearchInput value={q} onChange={(v) => { setQ(v); setOffset(0); }}
          placeholder="N° de facture, réf. ou client…" />
        <FilterSelect label="État" value={etat}
          onChange={(v) => { setEtat(v); setOffset(0); }}
          options={[['tout', 'Tous'], ['draft', 'Brouillon'], ['posted', 'Validée']]} />
        <FilterSelect label="Paiement" value={paiement}
          onChange={(v) => { setPaiement(v); setOffset(0); }}
          options={[['tout', 'Tous'], ['unpaid', 'Non payée'], ['overdue', 'En retard'], ['paid', 'Payée']]} />
        <button className="devis-refresh" onClick={reload} disabled={loading}>↻ Rafraîchir</button>
      </div>
      <div className="devis-body">
        <div className="devis-list">
          {/* Élément ouvert par lien direct : il n'est pas forcément dans la page
              listée — sur mobile, où le détail se déplie sous sa ligne, il faut
              alors l'afficher au-dessus de la liste. */}
          {isMobile && selectedId != null && !items.some((x: { id: number }) => x.id === selectedId) && detailPanel}
          {loading && <div className="devis-empty">Chargement…</div>}
          {error && !loading && <div className="devis-empty err">Erreur : {error}</div>}
          {!loading && !error && items.length === 0 && <div className="devis-empty">Aucune facture.</div>}
          {!loading && !error && items.length > 0 && (
            <table>
              <thead><tr>
                <SortTh col="numero" label="N°" sort={sort} dir={dir} onSort={onSort} />
                <SortTh col="client" label="Client" sort={sort} dir={dir} onSort={onSort} />
                <SortTh col="montant" label="Montant" className="num" sort={sort} dir={dir} onSort={onSort} />
                <SortTh col="date" label="Date" sort={sort} dir={dir} onSort={onSort} />
                <SortTh col="etat" label="État" sort={sort} dir={dir} onSort={onSort} />
              </tr></thead>
              <tbody>
                {items.map((f) => (
                  <Fragment key={f.id}>
                  <tr className={f.id === selectedId ? 'selected' : ''} onClick={() => setSelectedId(f.id)}>
                    <td className="mono cell-title">{f.name}</td>
                    <td data-label="Client">{f.partner ?? '—'}</td>
                    <td className="num" data-label="Montant">{fmtMoney(f.amount_total, f.currency)}</td>
                    <td data-label="Date"><BusinessDate value={f.invoice_date} createdAt={f.created_at} /></td>
                    <td data-label="État"><InvoiceBadge state={f.state} payment={f.payment_state} overdue={f.overdue} /></td>
                  </tr>
                  {/* Mobile : pas de colonne de droite, le détail s'ouvre sous la carte touchée. */}
                  {isMobile && f.id === selectedId && (
                    <tr className="detail-row detail-row-panel">
                      <td colSpan={5}>{detailPanel}</td>
                    </tr>
                  )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          )}
          {!loading && !error && (
            <Pager
              total={total} limit={PAGE_SIZE} offset={offset}
              onOffset={(o) => { setOffset(o); setSelectedId(null); }}
              unit={['facture', 'factures']}
            />
          )}
        </div>

        {!isMobile && detailPanel}
      </div>
    </>
  );
}

// ---------- Onglet Suivi (workflow unifié par vente) ----------
function SuiviTab() {
  // Le filtre part au serveur : l'étape d'une vente se déduit de l'état de sa
  // facture, donc filtrer sur les seules lignes déjà chargées donnait un décompte
  // faux dès que la liste dépassait le plafond de lecture.
  const [filter, setFilter] = useState<DealFilter>('en_cours');
  const [origine, setOrigine] = useState<OrigineFilter>('tout');
  const load = useCallback(
    (o: { limit: number; offset: number }) => api.getPipeline({ ...o, filter, origine }),
    [filter, origine],
  );
  const { items, total, counts, offset, setOffset, loading, error, reload } =
    usePagedList<DealRow, DealCounts>(load);
  const [selectedId, setSelectedId] = useSelectedParam('vente');
  useScrollToDetail(selectedId);
  const isMobile = useIsMobile();
  const [detail, setDetail] = useState<DealDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [toast, setToast] = useState<Toast>(null);
  const ask = useAskAssistant(setToast);


  function loadDeal(id: number) {
    setDetailLoading(true);
    api.getDeal(id).then(setDetail).catch((e) => setToast({ kind: 'err', text: e.message })).finally(() => setDetailLoading(false));
  }
  useEffect(() => {
    if (selectedId == null) { setDetail(null); return; }
    setDetail(null);
    loadDeal(selectedId);
  }, [selectedId]);

  function askForDeal() {
    if (!detail) return;
    const lines = detail.lines.map((l) => `  - ${l.qty}× ${l.name} — P.U. ${fmtMoney(l.price_unit, null)} — sous-total ${fmtMoney(l.subtotal, detail.currency)}`).join('\n');
    const kindLabel = detail.doc_key.startsWith('account.move') ? 'la facture' : detail.order_state === 'sale' ? 'la commande' : 'le devis';
    const prefix =
      `Je travaille sur ${kindLabel} Odoo **${detail.name}** (${detail.doc_key}).\n` +
      `- Client : ${detail.partner ?? '—'}\n` +
      `- Total : ${fmtMoney(detail.amount_total, detail.currency)}\n` +
      `- Lignes :\n${lines}\n\nMa demande : `;
    ask(detail.doc_key, `${detail.name}`, prefix);
  }

  // Exécute la prochaine étape en appelant l'endpoint correspondant au type d'action.
  async function doAction(action: string, confirmMsg: string, call: () => Promise<any>, ok: (r: any) => string) {
    if (!detail || busy) return;
    if (!await confirmer(confirmMsg)) return;
    setBusy(action);
    setToast(null);
    try {
      const res = await call();
      setToast({ kind: 'ok', text: ok(res) });
      loadDeal(detail.id);
      reload();
    } catch (e: any) {
      setToast({ kind: 'err', text: e.message });
    } finally {
      setBusy(null);
    }
  }

  function NextStep({ d }: { d: DealDetail }) {
    const inv = d.invoice;
    switch (d.next.kind) {
      case 'validate_quote':
        // Confirmer et prévenir le client sont deux décisions : la commande peut
        // avoir été confirmée par téléphone, ou l'adresse rester à vérifier. Les
        // deux gestes sont donc séparés (2026-10-28) — l'envoi reste disponible
        // après coup, dans les actions de la fiche.
        return (
          <button className="next-btn" disabled={busy != null}
            onClick={() => doAction('q', 'Confirmer cette commande ? Le client ne sera pas prévenu — l\u2019envoi de la confirmation est un bouton à part.',
              () => api.validateQuote(d.id, false), (r) => `${r.name} confirmée. ✅`)}>
            {busy ? 'Traitement…' : '▶ Valider la commande'}
          </button>
        );
      case 'validate_order':
        return (
          <button className="next-btn" disabled={busy != null}
            onClick={() => doAction('o', 'Valider la commande ? Livraison (sortie de stock) puis création de la facture à valider.',
              () => api.validateOrder(d.id), (r) => `${r.name} livrée${r.backorder ? ' (reliquat créé)' : ''}. ${r.invoice_name ? `Facture ${r.invoice_name}` : 'Facture'} créée. ✅`)}>
            {busy ? 'Traitement…' : `▶ ${d.next.label}`}
          </button>
        );
      case 'post_invoice':
        return inv ? (
          <button className="next-btn" disabled={busy != null}
            onClick={() => doAction('p', 'Valider (comptabiliser) la facture ?',
              () => api.postInvoice(inv.id), (r) => `Facture ${r.name ?? ''} validée. ✅`)}>
            {busy ? 'Traitement…' : `▶ ${d.next.label}`}
          </button>
        ) : null;
      case 'invoice_actions':
        return inv ? (
          <div className="next-multi">
            <button className="devis-ask" disabled={busy != null || !d.partner_email}
              onClick={() => doAction('s', `Envoyer la facture PDF à ${d.partner_email} ?`,
                () => api.sendInvoice(inv.id), () => 'Facture envoyée au client. ✅')}>
              {busy === 's' ? 'Envoi…' : '✉️ Envoyer au client'}
            </button>
            <button className="next-btn" disabled={busy != null}
              onClick={() => doAction('pay', 'Enregistrer le paiement de cette facture (marquer payée) ?',
                () => api.payInvoice(inv.id), () => 'Paiement enregistré. ✅')}>
              {busy === 'pay' ? 'Enregistrement…' : '▶ Marquer comme payée'}
            </button>
          </div>
        ) : null;
      default:
        // Vente soldée : rien à faire, SAUF revenir en arrière si l'encaissement a
        // été enregistré par erreur. Sans ce bouton, l'onglet Suivi — celui qu'on
        // ouvre par défaut — n'offrait aucune sortie sur une facture payée à tort.
        return (
          <div className="next-done">
            ✓ Vente terminée — rien à faire.
            {inv && (
              <button className="devis-ask" style={{ marginLeft: 10 }} disabled={busy != null}
                onClick={() => doAction('unpay',
                  'Annuler l\u2019encaissement de cette facture ? Elle redeviendra « non payée ».',
                  () => api.unpayInvoice(inv.id), () => 'Encaissement annulé. ✅')}>
                {busy === 'unpay' ? 'Annulation…' : '↩ Annuler le paiement'}
              </button>
            )}
          </div>
        );
    }
  }

  const detailPanel = selectedId == null ? null : (
    <aside className="devis-detail">
      <button className="devis-detail-close" onClick={() => setSelectedId(null)} title="Fermer">×</button>
      {detailLoading && <div className="devis-empty">Chargement…</div>}
      {detail && (
        <>
          <h2>{detail.name}</h2>
          <Stepper stage={detail.stage} source={detail.source} />
          <div className="devis-meta">
            <div><span>Client</span><strong>{detail.partner ?? '—'}</strong></div>
            {/* L'adresse est affichée ici parce que c'est depuis cette fiche que
                partent la confirmation de commande et la facture (ticket #40) :
                il faut voir à QUI, et voir tout de suite qu'il n'y a personne. */}
            <div><span>Email</span>{detail.partner_email
              ? <strong>{detail.partner_email}</strong>
              : <strong className="warn">aucun email</strong>}</div>
            <div><span>Livraison</span><strong><DeliveryStatusBadge status={detail.delivery_status} /></strong></div>
            {detail.expected_date && detail.stage < 3 && (
              <div><span>Livraison prévue</span><strong>{fmtDate(detail.expected_date)}{detail.late && <span className="status-badge tone-danger" style={{ marginLeft: 6 }}>En retard</span>}</strong></div>
            )}
            <div><span>Facturation</span><strong><InvoiceStatusBadge status={detail.invoice_status} /></strong></div>
            {detail.invoice && (
              <div><span>Facture</span><strong>
                <Link className="link-btn" to={`/ventes?tab=factures&facture=${detail.invoice.id}`}>{detail.invoice.name}</Link>
                {' '}<InvoiceBadge state={detail.invoice.state} payment={detail.invoice.payment_state} overdue={detail.invoice.overdue} />
              </strong></div>
            )}
            {detail.invoice && detail.invoice.state === 'posted' && detail.invoice.payment_state !== 'paid' && (
              <div><span>Restant dû</span><strong>{fmtMoney(detail.invoice.amount_residual, detail.currency)}</strong></div>
            )}
            <div><span>Date</span><strong><BusinessDate value={detail.date_order} createdAt={detail.created_at} /></strong></div>
            <SourceRow source={detail.source} />
            {detail.client_order_ref && <div><span>Réf. client</span><strong>{detail.client_order_ref}</strong></div>}
          </div>
          <AddressBlock detail={detail as unknown as OrderDetail} />
          {detail.stage < 3 ? (
            <table className="devis-lines">
              <thead>
                <tr><th>Article</th><th className="num">Qté</th><th className="num">Dispo</th><th className="num">P.U.</th><th className="num">Sous-total</th></tr>
              </thead>
              <tbody>
                {detail.lines.map((l) => (
                  <tr key={l.id}>
                    <td><ProductLink id={l.product_id}>{l.name}</ProductLink></td>
                    <td className="num">{l.qty}</td>
                    <td className={`num ${l.shortage ? 'stock-short' : ''}`} title={l.shortage ? 'Stock insuffisant' : 'Disponible'}>{l.available}</td>
                    <td className="num">{fmtMoney(l.price_unit, null)}</td>
                    <td className="num">{fmtMoney(l.subtotal, null)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <LinesTable lines={detail.lines} />
          )}
          <Totals ut={detail.amount_untaxed} tax={detail.amount_tax} total={detail.amount_total} currency={detail.currency} />

          {detail.stock_shortage && (
            <div className="devis-warn">⚠️ Stock insuffisant pour au moins un article : la livraison fera passer le stock en négatif.</div>
          )}

          {/* Tant que la vente n'est pas soldée, il reste un email à envoyer —
              confirmation de commande ou facture. L'absence d'adresse est donc
              un blocage à signaler ici, pas une découverte au clic. */}
          {!detail.partner_email && (
            <div className="devis-warn">
              ⚠️ Ce client n'a pas d'adresse email : ni la confirmation de commande ni la facture ne
              peuvent lui être envoyées. Renseignez-la sur sa fiche depuis l'écran{' '}
              <Link className="link-btn" to="/clients">Clients</Link>.
            </div>
          )}

          <SentEmails emails={detail.emails} />

          <div className="next-step">
            <span className="next-step-label">Prochaine étape</span>
            <NextStep d={detail} />
          </div>

          <div className="devis-actions">
            <a className="devis-odoo" href={`/api/orders/${detail.id}/pdf`}>⬇ Devis / commande (PDF)</a>
            {(detail.delivery_status === 'full' || detail.delivery_status === 'partial') && (
              <a className="devis-odoo" href={`/api/orders/${detail.id}/delivery-pdf`}>⬇ Bon de livraison (PDF)</a>
            )}
            {detail.invoice && <a className="devis-odoo" href={`/api/invoices/${detail.invoice.id}/pdf`}>⬇ Facture (PDF)</a>}
            <a className="devis-odoo" href={detail.invoice ? detail.invoice.web_url : detail.web_url} target="_blank" rel="noopener noreferrer">↗ Ouvrir dans Odoo</a>
            {/* Disponible dès que la commande est confirmée, et rejouable : un
                premier envoi peut avoir échoué, ou le client redemander son
                accusé. Odoo refuse avant la confirmation — la confirmation
                décrit un engagement pris. */}
            {detail.stage >= 1 && (
              <button className="devis-ask" disabled={busy != null || !detail.partner_email}
                title={detail.partner_email ? undefined : 'Ce client n’a pas d’adresse email'}
                onClick={() => doAction('conf', `Envoyer la confirmation de commande à ${detail.partner_email} ?`,
                  () => api.sendOrderConfirmation(detail.id), (r) => `Confirmation envoyée à ${r.email}. ✅`)}>
                {busy === 'conf' ? 'Envoi…' : '✉️ Envoyer la confirmation de commande'}
              </button>
            )}
            <button className="devis-ask" onClick={askForDeal}>💬 Demander une modification</button>
          </div>
        </>
      )}
    </aside>
  );

  return (
    <>
      {toast && <div className={`devis-toast ${toast.kind}`} onClick={() => setToast(null)}>{toast.text}</div>}
      <div className="ventes-toolbar">
        <div className="suivi-filters">
          {([['en_cours', 'En cours'], ['tout', 'Tout'], ['terminees', 'Terminées']] as [DealFilter, string][]).map(([k, lbl]) => (
            <button
              key={k}
              className={`suivi-filter ${filter === k ? 'active' : ''}`}
              onClick={() => { setFilter(k); setOffset(0); setSelectedId(null); }}
            >
              {lbl}{counts ? ` (${counts[k]})` : ''}
            </button>
          ))}
        </div>
        {/* Origine : sépare les commandes transcrites d'un email des propales saisies
            à la main. Les compteurs d'étape ci-dessus décrivent l'origine choisie. */}
        <div className="suivi-filters origine-filters">
          {([['tout', 'Toutes origines', 'origine_tout'], ['agent', '🤖 Agent', 'agent'], ['manuel', '✍️ Manuel', 'manuel']] as [OrigineFilter, string, keyof DealCounts][]).map(([k, lbl, ck]) => (
            <button
              key={k}
              className={`suivi-filter ${origine === k ? 'active' : ''}`}
              onClick={() => { setOrigine(k); setOffset(0); setSelectedId(null); }}
            >
              {lbl}{counts ? ` (${counts[ck]})` : ''}
            </button>
          ))}
        </div>
        <button className="devis-refresh" onClick={reload} disabled={loading}>↻ Rafraîchir</button>
      </div>
      <div className="devis-body">
        <div className="devis-list">
          {/* Élément ouvert par lien direct : il n'est pas forcément dans la page
              listée — sur mobile, où le détail se déplie sous sa ligne, il faut
              alors l'afficher au-dessus de la liste. */}
          {isMobile && selectedId != null && !items.some((x: { id: number }) => x.id === selectedId) && detailPanel}
          {loading && <div className="devis-empty">Chargement…</div>}
          {error && !loading && <div className="devis-empty err">Erreur : {error}</div>}
          {!loading && !error && items.length === 0 && <div className="devis-empty">Aucune vente à afficher.</div>}
          {!loading && !error && items.length > 0 && (
            <table>
              <thead><tr><th>N°</th><th>Client</th><th className="num">Montant</th><th>Avancement</th><th>Prochaine étape</th></tr></thead>
              <tbody>
                {items.map((d) => (
                  <Fragment key={d.id}>
                  <tr className={d.id === selectedId ? 'selected' : ''} onClick={() => setSelectedId(d.id)}>
                    <td className="mono cell-title">
                      {d.name} <SourceBadge source={d.source} />
                      {/* Le N° de la vente ne suffit plus une fois la facture émise :
                          c'est son numéro que la personne cherche et recopie. Le lien
                          ouvre la facture dans son onglet, sans quitter le suivi. */}
                      {d.invoice && (
                        <div className="suivi-doc">
                          <Link
                            to={`/ventes?tab=factures&facture=${d.invoice.id}`}
                            onClick={(e) => e.stopPropagation()}
                            title="Ouvrir cette facture"
                          >{d.invoice.name}</Link>
                        </div>
                      )}
                    </td>
                    <td data-label="Client">{d.partner ?? '—'}</td>
                    <td className="num" data-label="Montant">{fmtMoney(d.amount_total, d.currency)}</td>
                    <td data-label="Avancement"><Stepper stage={d.stage} source={d.source} compact /></td>
                    <td className="suivi-next" data-label="Prochaine étape">{d.stage >= 5 ? '✓ Terminé' : d.next_label}{(d.late || d.overdue) && <span className="suivi-overdue"> · ⏰ en retard</span>}</td>
                  </tr>
                  {/* Mobile : pas de colonne de droite, le détail s'ouvre sous la carte touchée. */}
                  {isMobile && d.id === selectedId && (
                    <tr className="detail-row detail-row-panel">
                      <td colSpan={5}>{detailPanel}</td>
                    </tr>
                  )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          )}
          {!loading && !error && (
            <Pager
              total={total} limit={PAGE_SIZE} offset={offset}
              onOffset={(o) => { setOffset(o); setSelectedId(null); }}
              unit={['vente', 'ventes']}
              capped={counts?.capped}
              cappedNote="au-delà de 1000 ventes, les plus anciennes ne sont pas lues"
            />
          )}
        </div>

        {!isMobile && detailPanel}
      </div>
    </>
  );
}

// ---------- Page hôte ----------
const TABS = [
  { key: 'suivi', label: 'Suivi' },
  { key: 'devis', label: 'Devis' },
  { key: 'commandes', label: 'Commandes' },
  { key: 'factures', label: 'Factures' },
] as const;
type TabKey = (typeof TABS)[number]['key'];

export default function Ventes() {
  const [params, setParams] = useSearchParams();
  const raw = params.get('tab');
  const tab: TabKey = TABS.some((t) => t.key === raw) ? (raw as TabKey) : 'suivi';

  function setTab(k: TabKey) {
    setParams(k === 'suivi' ? {} : { tab: k }, { replace: true });
  }

  return (
    <div className="devis-page">
      <div className="ventes-tabs" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.key}
            role="tab"
            aria-selected={tab === t.key}
            className={`ventes-tab ${tab === t.key ? 'active' : ''}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'suivi' && <SuiviTab />}
      {tab === 'devis' && <DevisTab />}
      {tab === 'commandes' && <CommandesTab />}
      {tab === 'factures' && <FacturesTab />}
    </div>
  );
}
